import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { type Befehl, sucheBefehle } from '@schnitt/shared';
import { Sheet } from './Sheet';

export interface BefehlMitTun extends Befehl {
  tun: () => void;
}

/**
 * Suchen und ausführen (S27). Sascha: „Ein Feld, in das man tippt, was man
 * will (‚trennen‘, ‚exportieren‘), statt es zu suchen.“
 *
 * Das Feld hat den Finger, sobald es aufgeht -- wer es öffnet, will tippen.
 * Pfeile wählen, Eingabe führt aus, Esc schliesst; auf dem Telefon tippt man
 * den Eintrag an. Gesucht wird in `sucheBefehle` im gemeinsamen Teil, geprüft
 * ohne Browser: was oben steht, entscheidet über die Nützlichkeit.
 */
export function BefehlSheet({
  befehle,
  onClose,
}: {
  befehle: BefehlMitTun[];
  onClose: () => void;
}): ReactElement {
  const [frage, setFrage] = useState('');
  const [wahl, setWahl] = useState(0);
  const feld = useRef<HTMLInputElement>(null);

  const treffer = useMemo(() => sucheBefehle(befehle, frage), [befehle, frage]);
  // Wer tippt, fängt oben an: nach jeder Änderung steht die Wahl auf dem
  // ersten Treffer. Sonst zeigte der Balken auf den dritten Eintrag einer
  // Liste, die es nicht mehr gibt.
  useEffect(() => setWahl(0), [frage]);
  useEffect(() => feld.current?.focus(), []);

  const fuehreAus = (befehl: BefehlMitTun | undefined) => {
    if (!befehl) return;
    // Erst schliessen, dann tun: mehrere Befehle öffnen selbst ein Blatt, und
    // zwei Blätter übereinander sind zwei „Fertig“-Knöpfe.
    onClose();
    befehl.tun();
  };

  return (
    <Sheet title="Was willst du tun?" onClose={onClose}>
      <input
        ref={feld}
        data-befehl-feld
        placeholder="trennen, exportieren, Untertitel…"
        value={frage}
        onChange={(e) => setFrage(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setWahl((w) => Math.min(treffer.length - 1, w + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setWahl((w) => Math.max(0, w - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            fuehreAus(treffer[wahl]?.befehl as BefehlMitTun | undefined);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
        style={{ width: '100%' }}
      />
      <div className="list" data-befehle={String(treffer.length)} style={{ marginTop: 10 }}>
        {treffer.length === 0 ? (
          <p className="hint">Dazu gibt es hier keinen Befehl. Andere Worte?</p>
        ) : null}
        {treffer.map((t, i) => (
          <button
            key={t.befehl.id}
            className={`row ${i === wahl ? 'active' : ''}`}
            data-befehl={t.befehl.id}
            aria-selected={i === wahl}
            style={{ textAlign: 'left', width: '100%' }}
            onMouseEnter={() => setWahl(i)}
            onClick={() => fuehreAus(t.befehl as BefehlMitTun)}
          >
            <div className="grow">
              <div className="title">{t.befehl.name}</div>
              {t.befehl.gruppe ? <div className="sub">{t.befehl.gruppe}</div> : null}
            </div>
            {t.befehl.taste ? <kbd className="badge">{t.befehl.taste}</kbd> : null}
          </button>
        ))}
      </div>
    </Sheet>
  );
}
